import { Feature } from "@core/enums";
import {
  type UnleashClient,
  useUnleashClient,
} from "@unleash/proxy-client-react";
import { Navigate } from "react-router-dom";

export enum FeatureGuardBehaviour {
  Hide = 1,
  NavigateToAccessDenied,
}

export enum ComplexFeatureGuardLogic {
  OneOf = "one-of",
  AllOf = "all-of",
  AnyOf = "any-of",
}

export type ComplexFeatureGuardLogicProp = `${ComplexFeatureGuardLogic}`;

export type FeatureGuardBaseProps = React.PropsWithChildren<{
  behaviour?: FeatureGuardBehaviour;
}>;

export type FeatureGuardProps = FeatureGuardBaseProps & {
  features: Feature | Feature[] | undefined;
  logic?: ComplexFeatureGuardLogicProp;
};

export function canAccessFeatures(
  client: UnleashClient,
  features: Feature[],
  logic: ComplexFeatureGuardLogicProp,
): boolean {
  const featureIsEnabled = (feature: Feature) => client.isEnabled(feature);

  switch (logic) {
    case ComplexFeatureGuardLogic.OneOf:
      return features.filter(featureIsEnabled).length === 1;

    case ComplexFeatureGuardLogic.AllOf:
      return features.every(featureIsEnabled);

    case ComplexFeatureGuardLogic.AnyOf:
      return features.some(featureIsEnabled);

    default:
      return false;
  }
}

export const FeatureGuard = ({
  children,
  behaviour = FeatureGuardBehaviour.Hide,
  features,
  logic = ComplexFeatureGuardLogic.AllOf,
}: FeatureGuardProps) => {
  localStorage.removeItem("unleash:repository:repo");
  localStorage.removeItem("unleash:repository:sessionId");

  const parsedFeatures =
    !!features && !Array.isArray(features)
      ? [features]
      : Array.isArray(features)
        ? features
        : [];
  const client = useUnleashClient();

  if (parsedFeatures.length == 0) {
    return children;
  }
  const enabled = canAccessFeatures(client, parsedFeatures, logic);

  if (!enabled && behaviour === FeatureGuardBehaviour.Hide) return null;

  if (behaviour === FeatureGuardBehaviour.NavigateToAccessDenied && !enabled) {
    return <Navigate to="/access-denied" />;
  }

  return children;
};
